import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getDatasetsFilesType, useDataSourcesModal } from '@mygpt/app';
import { getDatasetGbaseProjects } from '@mygpt/app/apis';
import { GbaseConnectModal } from './GbaseConnectModal';

import {
  SvgAPIList,
  SvgAPIListActive,
  SvgDocument,
  SvgDocumentActive,
  SvgFAQList,
  SvgFAQListActive,
  SvgGitbook,
  SvgGitbookActive,
  SvgIntegration,
  SvgIntegrationActive,
  SvgSitemap,
  SvgSitemapActive,
  SvgWebsite,
  SvgWebsiteActive,
} from '@/components/Create_V2/Container/icon';
import DataSources from '@/components/Datasets/DataSources';
import { useDatasetStore } from '@/store/dataset';
import { filterAdvancedDatasources, filterTabs } from '@/utils/filters';

export const DataSourcesPage = () => {
  const { '*': activeType } = useParams();

  const { datasetId } = useParams();
  const navigate = useNavigate();
  const { counter, gbaseModalOpened, openGbaseModal, closeGbaseModal, setCounter } = useDataSourcesModal();
  const location = useLocation();
  const { pathname, search } = location;

  const { t, i18n } = useTranslation();
  const { count: filesInfo, init: initFilesInfo, resetCount, setCount } = useDatasetStore();

  // 关闭GBase弹窗并触发刷新
  const handleCloseGbaseModal = () => {
    closeGbaseModal();
    setCounter(counter + 1);
  };

  // 检查 URL 参数，判断是否显示弹窗
  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const isOpen = queryParams.get('isOpen');
    if (isOpen === 'true') {
      openGbaseModal();
      // 清除 isOpen 参数，避免刷新页面时重复触发
      queryParams.delete('isOpen');
      const newSearch = queryParams.toString();
      const newUrl = newSearch ? `${pathname}?${newSearch}` : pathname;
      navigate(newUrl, { replace: true });
    }
  }, [search, pathname, navigate, openGbaseModal]);

  // 用于竞态保护：追踪当前 datasetId，丢弃过期的 API 响应
  const currentDatasetIdRef = useRef(datasetId);
  currentDatasetIdRef.current = datasetId;

  useEffect(() => {
    resetCount();
  }, [datasetId]);

  const sourcesTypeList = useMemo(() => {
    return [
      {
        type: 'file',
        title: t('dataset.source_title.file'),
        icon: SvgDocument,
        activeIcon: SvgDocumentActive,
        counts: filesInfo.file,
      },
      {
        type: 'website',
        title: t('dataset.source_title.website'),
        icon: SvgWebsite,
        activeIcon: SvgWebsiteActive,
        counts: filesInfo.website,
      },
      {
        type: 'web-connector',
        title: t('dataset.source_title.website'),
        icon: SvgWebsite,
        activeIcon: SvgWebsiteActive,
        counts: filesInfo.integration.web_connector,
        showProBadge: true,
      },
      {
        type: 'sitemap',
        title: t('dataset.source_title.sitemap'),
        icon: SvgSitemap,
        activeIcon: SvgSitemapActive,
        counts: filesInfo.sitemap,
      },
      {
        type: 'gitbook',
        title: t('dataset.source_title.gitbook'),
        icon: SvgGitbook,
        activeIcon: SvgGitbookActive,
        counts: filesInfo.gitbook,
      },
      {
        type: 'faq',
        title: t('dataset.source_title.faq'),
        icon: SvgFAQList,
        activeIcon: SvgFAQListActive,
        counts: filesInfo.faq,
      },
      {
        type: 'api',
        title: t('dataset.source_title.api'),
        icon: SvgAPIList,
        activeIcon: SvgAPIListActive,
        counts: filesInfo.api,
      },
      {
        type: 'integration',
        title: t('create_v2.integrations.title'),
        icon: SvgIntegration,
        activeIcon: SvgIntegrationActive,
        counts: (filesInfo.integration.lark || 0) + (filesInfo.integration.gbase_project || 0),
      },
      {
        // Zendesk 工单 → 蒸馏 FAQ（知识环数据源）。一期不显示计数角标；部署侧 HIDE 开关可隐藏
        type: 'zendesk',
        title: t('dataset.source_title.zendesk'),
        icon: SvgIntegration,
        activeIcon: SvgIntegrationActive,
        counts: 0,
      },
    ]
      .filter((item) => !(item.type === 'zendesk' && window.ENV?.GBASE_SUPPORT_FRONTEND_HIDE_ZENDESK === 'true'))
      .filter(filterTabs)
      .filter(filterAdvancedDatasources);
  }, [filesInfo, t]);

  const initInfo = async () => {
    if (!datasetId) return;
    const requestDatasetId = datasetId;
    try {
      const res = await getDatasetsFilesType({ id: datasetId, language: i18n.language });

      // 竞态保护：如果响应返回时 datasetId 已变化，丢弃该响应
      if (currentDatasetIdRef.current !== requestDatasetId) return;

      // 先获取GBase Project实际数量
      let gbaseCount = 0;
      try {
        const gbaseRes = await getDatasetGbaseProjects(datasetId);
        gbaseCount = gbaseRes.data?.length || 0;
      } catch (error) {
        console.error('获取GBase Project数量失败:', error);
      }

      // 补充gbase_project字段到后端数据中
      const dataWithGbase = {
        ...res.data,
        integration: {
          ...res.data.integration,
          gbase_project: gbaseCount,
        },
      };

      // 使用补充后的数据初始化
      initFilesInfo(dataWithGbase);
    } catch (err) {
      console.error('获取知识库数据的数量数据失败：', err);
    }
  };

  useEffect(() => {
    if (!activeType) {
      navigate(`${pathname}${pathname.charAt(pathname.length - 1) === '/' ? '' : '/'}${sourcesTypeList[0].type}`, {
        replace: true,
      });
    } else {
      initInfo();
    }
  }, [pathname, activeType]);

  useEffect(() => {
    if (counter > 0) {
      initInfo();
    }
  }, [counter]);

  return (
    <>
      <DataSources SourcesTypeList={sourcesTypeList} />
      <GbaseConnectModal opened={gbaseModalOpened} onClose={handleCloseGbaseModal} datasetId={datasetId || ''} />
    </>
  );
};
